import React, { useEffect, useState } from 'react';
import { Spin, Tag, Button, Drawer, Select, Popconfirm, Typography, Badge, Tooltip, Input } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import moment from 'moment';
import useFlowEnrollmentStore from '../../stores/useFlowEnrollmentStore';
import useModuleGroupStore from '../../stores/useModuleGroupStore';

const SOURCE_COLORS = { campaign: 'purple', bot: 'blue', manual: 'green' };

function bfsModuleOrder(modules, startModuleId) {
    const adjMap = {};
    modules.forEach((m) => {
        const connGotos = (m.connection || []).map((c) => c.goto);
        const triggerGotos = (m.triggers || [])
            .filter((t) => !t.deleted_at && t.goto_module_id)
            .map((t) => t.goto_module_id);
        adjMap[m.id] = connGotos.concat(triggerGotos);
    });

    const visited = new Set();
    const order = [];
    const queue = [startModuleId];
    while (queue.length) {
        const current = queue.shift();
        if (visited.has(current)) continue;
        visited.add(current);
        order.push(current);
        (adjMap[current] || []).forEach((nextId) => {
            if (!visited.has(nextId)) queue.push(nextId);
        });
    }
    // Append any modules not reachable from start
    modules.forEach((m) => {
        if (!visited.has(m.id)) order.push(m.id);
    });
    return order;
}

export default function FlowEnrollmentBoard({ modulegroupId, campaignId, flowSnapshot, moduleResponseOverrides, onCardClick, highlightContactId }) {
    const { moduleGroup, modules, fetchModuleGroup } = useModuleGroupStore();
    const {
        boardData,
        boardLoading,
        fetchBoard,
        selectedEnrollment,
        setSelectedEnrollment,
        moveEnrollment,
        dropEnrollment,
        isStalled,
    } = useFlowEnrollmentStore();

    const [dropReason, setDropReason] = useState('');
    const boardKey = campaignId ? `${modulegroupId}:${campaignId}` : modulegroupId;

    useEffect(() => {
        if (modulegroupId) {
            fetchBoard(modulegroupId, campaignId);
            // Ensure module data is loaded for column headers
            if (!modules || modules.length === 0 || moduleGroup?.id !== modulegroupId) {
                fetchModuleGroup(modulegroupId);
            }
        }
    }, [modulegroupId, campaignId]);

    if (!modules || modules.length === 0) {
        return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spin /></div>;
    }

    const isLoading = boardLoading[boardKey];
    const enrollments = boardData[boardKey] || [];

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                <Spin />
            </div>
        );
    }

    const startModuleId = moduleGroup ? moduleGroup.module_id : (modules[0] ? modules[0].id : null);
    const orderedModuleIds = startModuleId ? bfsModuleOrder(modules, startModuleId) : modules.map((m) => m.id);
    const moduleMap = {};
    modules.forEach((m) => { moduleMap[m.id] = m; });

    // Build a snapshot lookup for tooltip text (snapshot has overrides baked in)
    const snapshotMap = {};
    if (flowSnapshot && flowSnapshot.modules) {
        flowSnapshot.modules.forEach((m) => { snapshotMap[m.id] = m; });
    }

    const getModuleTooltip = (id) => {
        // Prefer snapshot (has overrides already baked in)
        const snap = snapshotMap[id];
        if (snap) {
            const text = snap.response || '';
            const append = snap.response_append || '';
            return append ? `${text}\n${append}` : text;
        }
        // Fallback: live module + manual override
        const mod = moduleMap[id];
        if (!mod) return null;
        const overrides = moduleResponseOverrides || {};
        const response = (overrides[id] !== undefined && mod.response_user_modify)
            ? overrides[id]
            : (mod.response || '');
        const append = mod.response_append || '';
        return append ? `${response}\n${append}` : response;
    };

    const columns = [
        ...orderedModuleIds.map((id) => ({
            key: id,
            label: moduleMap[id] ? moduleMap[id].title : id,
            tooltip: getModuleTooltip(id),
            cards: enrollments.filter((e) => (e.status === 'active' || e.status === 'completed') && e.current_module_id === id),
        })),
        {
            key: 'dropped',
            label: 'Dropped',
            cards: enrollments.filter((e) => e.status === 'dropped'),
        },
    ];

    const renderCard = (enrollment) => {
        const stalled = isStalled(enrollment);
        const isCompleted = enrollment.status === 'completed';
        const isHighlighted = highlightContactId && enrollment.contact_id === highlightContactId;
        return (
            <div
                key={enrollment.id}
                onClick={() => onCardClick ? onCardClick(enrollment) : setSelectedEnrollment(enrollment)}
                style={{
                    border: isHighlighted ? '2px solid #1677ff' : '1px solid #f0f0f0',
                    borderLeft: stalled ? '3px solid #fa8c16' : isCompleted ? '3px solid #52c41a' : (isHighlighted ? '2px solid #1677ff' : '1px solid #f0f0f0'),
                    borderRadius: 6,
                    padding: '8px 10px',
                    marginBottom: 8,
                    background: isHighlighted ? '#e6f4ff' : isCompleted ? '#f6ffed' : '#fff',
                    boxShadow: isHighlighted ? '0 0 0 4px rgba(22, 119, 255, 0.15)' : 'none',
                    cursor: 'pointer',
                }}
            >
                <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {enrollment.contact_name}
                    {isCompleted && <CheckCircleFilled style={{ color: '#52c41a', fontSize: 12 }} />}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Tag
                        color={SOURCE_COLORS[enrollment.source] || 'default'}
                        style={{ fontSize: 11, margin: 0 }}
                    >
                        {enrollment.source}
                    </Tag>
                    <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                        {moment(enrollment.created_at).fromNow()}
                    </span>
                </div>
                {enrollment.status === 'dropped' && enrollment.drop_reason && (
                    <div style={{ fontSize: 11, color: '#8c8c8c', fontStyle: 'italic', marginTop: 4 }}>
                        {enrollment.drop_reason}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{ padding: 16 }}>
            {/* Horizontal scroll board */}
            <div style={{ display: 'flex', gap: 12, overflowX: 'auto', alignItems: 'flex-start', paddingBottom: 16 }}>
                {columns.map((col) => (
                    <div
                        key={col.key}
                        style={{
                            minWidth: 220,
                            width: 220,
                            flexShrink: 0,
                            background: '#f5f5f5',
                            borderRadius: 8,
                            padding: '10px 10px 4px 10px',
                        }}
                    >
                        {/* Column header */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <Tooltip title={col.tooltip ? <span style={{ whiteSpace: 'pre-wrap' }}>{col.tooltip}</span> : null} placement="top">
                                <span style={{ fontWeight: 600, fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.label}</span>
                            </Tooltip>
                            <Badge
                                count={col.cards.length}
                                showZero
                                style={{ backgroundColor: '#d9d9d9', color: '#595959' }}
                            />
                        </div>

                        {/* Cards */}
                        {col.cards.length === 0 ? (
                            <div style={{ fontSize: 12, color: '#bfbfbf', textAlign: 'center', padding: '12px 0' }}>
                                No enrollments
                            </div>
                        ) : (
                            col.cards.map((enrollment) => renderCard(enrollment))
                        )}
                    </div>
                ))}
            </div>

            {/* Detail Drawer (only when no external handler) */}
            {!onCardClick && <Drawer
                title={selectedEnrollment ? selectedEnrollment.contact_name : ''}
                open={!!selectedEnrollment}
                onClose={() => { setSelectedEnrollment(null); setDropReason(''); }}
                width={360}
                placement="right"
            >
                {selectedEnrollment && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div>
                            <a href={`/contacts/${selectedEnrollment.contact_id}`}>View Contact</a>
                        </div>
                        {selectedEnrollment.conversation_id && (
                            <div>
                                <a href={`/inbox?conversation=${selectedEnrollment.conversation_id}`}>Open Conversation</a>
                            </div>
                        )}

                        {selectedEnrollment.status === 'active' && (
                            <div>
                                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                    Move to module
                                </Typography.Text>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="Select module..."
                                    value={selectedEnrollment.current_module_id}
                                    onChange={(moduleId) => {
                                        moveEnrollment(selectedEnrollment.id, modulegroupId, moduleId, campaignId);
                                    }}
                                >
                                    {modules.map((m) => (
                                        <Select.Option key={m.id} value={m.id}>
                                            {m.title}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        )}

                        {selectedEnrollment.status === 'dropped' && selectedEnrollment.drop_reason && (
                            <div>
                                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                    Drop reason
                                </Typography.Text>
                                <Typography.Text style={{ fontStyle: 'italic' }}>
                                    {selectedEnrollment.drop_reason}
                                </Typography.Text>
                            </div>
                        )}

                        {selectedEnrollment.status === 'active' && (
                            <div>
                                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                    Drop reason (optional)
                                </Typography.Text>
                                <Input.TextArea
                                    rows={2}
                                    placeholder="Why is this enrollment being dropped?"
                                    value={dropReason}
                                    onChange={(e) => setDropReason(e.target.value)}
                                    style={{ marginBottom: 8 }}
                                />
                                <Popconfirm
                                    title="Mark this enrollment as dropped?"
                                    onConfirm={() => {
                                        dropEnrollment(selectedEnrollment.id, modulegroupId, campaignId, dropReason);
                                        setDropReason('');
                                    }}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button danger>Mark Dropped</Button>
                                </Popconfirm>
                            </div>
                        )}
                    </div>
                )}
            </Drawer>}
        </div>
    );
}
